import React, { useCallback } from "react";
import ContentLoader from "react-content-loader";
import { Translate } from "react-localize-redux";

import { Barber, StoreSelectedSource } from "../../types";
import { ReactComponent as IconArrowRight } from "../../assets/images/icon-arrow-right.svg";
import { loaderTheme } from "../../utils/loaders";
import { navigateTo } from "../../utils/navigation";
import { ReactComponent as FavoriteIcon } from "../../assets/images/icon-favorite-active.svg";
import classNames from "classnames";
import placeholderAvatar from "../../assets/images/avatar-placeholder.png";

export interface BarberCardProps extends Barber {
  source: StoreSelectedSource;
  onAfterClick?: () => void;
  isFavorite?: boolean;
}

const BarberCard: React.FC<BarberCardProps> = ({
  id,
  pob,
  onAfterClick,
  isFavorite,
  status,
  firstname,
  lastname,
  active_pob,
  image,
}) => {
  // Barber details

  const full_name = firstname && lastname ? `${firstname} ${lastname}` : "";
  const short_name = (() => {
    const first = firstname || (full_name ? full_name.split(" ")[0] : "");
    const last = lastname || (full_name ? full_name.split(" ").slice(-1)[0] : "");
    const lastInitial = last ? `${last.charAt(0).toUpperCase()}.` : "";
    return [first, lastInitial].filter(Boolean).join(" ").trim();
  })();
  const barber_image = image ? image : placeholderAvatar;

  const handleClick = useCallback(
    (e) => {
      e.preventDefault();

      // analytics.storeSelected({ ...name, id }, source);
      navigateTo(`/barbers/${id}`, "switch-open");

      if (onAfterClick) {
        onAfterClick();
      }
    },
    [onAfterClick, id]
  );

  const componentClass = classNames("c-barber-card");

  /**
   * @note There was a weird visual glitch in chrome where the cards would
   * sometimes disappear when scrolling, and re-appear when changing some css value.
   *
   * It _might_ have been caused by using a `<button>` element as the component
   * root, but not sure. Take care if using that.
   */

  return (
    <a href={`/barbers/${id}`} className={componentClass} onClick={handleClick}>
      <div className="c-barber-card__main">
        <div className="c-barber-card__image-wrapper">
          <img
            src={barber_image}
            alt=""
            className="c-barber-card__image"
            onError={(e) => {
              const target = e.currentTarget as HTMLImageElement;
              target.onerror = null;
              target.src = placeholderAvatar;
            }}
          />
        </div>
        <div className="c-barber-card__info">
          <h3 className="c-barber-card__title">
            {short_name}
            {isFavorite && (
              <span className="c-barber-card__favorite">
                <FavoriteIcon width={12} height={12} />
              </span>
            )}
          </h3>
          {active_pob && (
            <p className="c-barber-card__subtitle">
              <span className="c-barber-card__ellipse is-available"></span>
              {/* <Translate id="barber.now" /> -{" "} */}
              {`${active_pob?.name}, ${active_pob?.city}`}{" "}
            </p>
          )}
          {!active_pob && (
            <p className="c-barber-card__subtitle">
              <span className="c-barber-card__ellipse"></span>
              <Translate id="barber.notIn" />
            </p>
          )}
        </div>
      </div>

      <div className="c-barber-card__icon">
        <IconArrowRight />
      </div>
    </a>
  );
};

export default BarberCard;

interface MultiCardLoaderProps {
  amount?: number;
}

export const MultiBarberCardLoader: React.FC<MultiCardLoaderProps> = ({
  amount = 3,
}) => {
  return (
    <>
      {[...Array(amount)].map((_, index) => (
        <BarberCardLoader key={index} />
      ))}
    </>
  );
};

export const BarberCardLoader = () => (
  <ContentLoader
    viewBox="0 0 315 92"
    width={"100%"}
    height={92}
    {...loaderTheme}
    opacity={0.5}
    style={{ width: "100%", display: "block", marginBottom: 3 }}
    preserveAspectRatio="none"
  >
    <rect x="0" y="0" rx="5" ry="5" width="315" height="92" />
  </ContentLoader>
);
